/**
 * MoveFolderDialog — folder picker modal for the Documents tab "Move to
 * folder" affordance (DMF-1).
 *
 * Pre-DMF the `doc-move-${docId}` button rendered an inline popover that
 * silently fell through to the row-level click handler under certain
 * mount paths — clicking it navigated to `?tab=evidence` instead of
 * opening a folder picker. Replacing the popover with an explicit
 * modal dialog (this component) removes the popover-vs-row-click race
 * and gives the user a real folder picker UX: search/filter, every
 * existing folder, and inline "Create new folder".
 *
 * Persistence: the Save handler invokes the store's `moveSiteDocument`
 * action (DDL-3 cross-array parity — see `patchSiteDocument` in
 * src/store/index.ts) so the move lands on `site.documents[i]` AND
 * every `report.documents[i]` on the site. The SiteLandingPage union
 * dedupe can't restore the pre-move folder from a stale report copy.
 *
 * Empty `newFolder` is a valid value — it clears the user override and
 * lets `categorize()` route the doc back to its auto-derived bucket.
 */
import React, { useMemo, useState } from 'react';
import { FolderOpen, Plus, Search, X } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useStore } from '../../store';
import type { SiteDocument } from '../../types/siteRag';

interface Props {
  /** Site id — required, drives the store action. */
  siteId: string;
  /** The doc being moved. */
  doc: SiteDocument;
  /**
   * Existing folder names — typically derived in the caller from
   * `site.documents.map(d => d.folder).distinct()` (plus
   * `customFolders` so empty user-created buckets stay visible).
   * Sorted by the caller; this component renders the list verbatim
   * after applying the search filter.
   */
  folders: string[];
  open: boolean;
  onClose: () => void;
  /** Called after a successful move so the host can refresh side-effects (library index, toast routing, etc.). */
  onMoved?: (newFolder: string) => void;
}

export const MoveFolderDialog: React.FC<Props> = ({
  siteId,
  doc,
  folders,
  open,
  onClose,
  onMoved,
}) => {
  const moveSiteDocument = useStore(state => state.moveSiteDocument);
  const [selected, setSelected] = useState<string | null>(doc.folder ?? null);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset local state every time the dialog re-opens for a different doc.
  React.useEffect(() => {
    if (!open) return;
    setSelected(doc.folder ?? null);
    setSearch('');
    setCreating(false);
    setNewFolderName('');
    setBusy(false);
  }, [open, doc.id, doc.folder]);

  const filtered = useMemo(() => {
    if (!search.trim()) return folders;
    const q = search.trim().toLowerCase();
    return folders.filter(f => f.toLowerCase().includes(q));
  }, [folders, search]);

  if (!open) return null;

  const handleSave = async () => {
    if (busy) return;
    const target = creating ? newFolderName.trim() : selected;
    if (!target) {
      toast.error('Pick a folder or create a new one');
      return;
    }
    if (target === doc.folder) {
      // No-op — close without firing the store action.
      onClose();
      return;
    }
    setBusy(true);
    try {
      await moveSiteDocument(siteId, doc.id, target);
      toast.success(`Moved to "${target}"`);
      onMoved?.(target);
      onClose();
    } catch (e) {
      toast.error(`Move failed: ${e instanceof Error ? e.message : 'unknown'}`);
    } finally {
      setBusy(false);
    }
  };

  const canSave = creating ? newFolderName.trim().length > 0 : selected !== null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={onClose}
      data-testid={`move-folder-dialog-${doc.id}`}
    >
      <div
        className="bg-background w-[calc(100vw-2rem)] max-w-[480px] max-h-[80vh] overflow-y-auto shadow-2xl border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-primary/40 bg-primary/10 flex items-center justify-center shrink-0">
            <FolderOpen className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary">
              Move to folder
            </h2>
            <p
              className="text-[10px] text-text-secondary mt-0.5 truncate"
              title={doc.fileName}
            >
              {doc.fileName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-text-muted hover:text-text-primary"
            title="Close"
            data-testid={`move-folder-dialog-close-${doc.id}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        {!creating && (
          <div className="px-5 pt-4 pb-2">
            <label className="type-section text-text-secondary mb-1.5 block">
              Pick a folder
            </label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-text-muted absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search folders…"
                className="w-full text-[11px] pl-8 pr-3 py-2 border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                data-testid={`move-folder-dialog-search-${doc.id}`}
              />
            </div>
          </div>
        )}

        {/* Folder list */}
        {!creating && (
          <div className="px-5 pb-3">
            <div
              className="max-h-[260px] overflow-y-auto border border-border-subtle"
              data-testid={`move-folder-dialog-list-${doc.id}`}
            >
              {filtered.length === 0 ? (
                <div className="text-[10px] text-text-muted italic px-3 py-3">
                  No folders match — clear the search or create a new folder below.
                </div>
              ) : (
                filtered.map((f) => {
                  const isSelected = selected === f;
                  const isCurrent = doc.folder === f;
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setSelected(f)}
                      className={`w-full text-left text-[11px] px-3 py-2 flex items-center gap-2 transition-colors border-b border-border-subtle last:border-b-0 ${
                        isSelected
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-state-hover text-text-primary'
                      }`}
                      data-testid={`move-folder-dialog-option-${doc.id}-${f}`}
                    >
                      <FolderOpen
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'text-primary' : 'text-warning'
                        }`}
                      />
                      <span className="truncate flex-1">{f}</span>
                      {isCurrent && (
                        <span className="type-body uppercase tracking-widest text-text-muted shrink-0">
                          current
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Create new folder */}
        <div className="px-5 pb-3">
          {creating ? (
            <div>
              <label className="type-section text-text-secondary mb-1.5 block">
                New folder name
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="e.g. Permitting"
                  className="flex-1 text-[11px] px-3 py-2 border border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  autoFocus
                  data-testid={`move-folder-dialog-new-input-${doc.id}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newFolderName.trim().length > 0) {
                      void handleSave();
                    }
                  }}
                />
                <button
                  onClick={() => {
                    setCreating(false);
                    setNewFolderName('');
                  }}
                  className="p-2 text-text-secondary hover:text-text-primary"
                  title="Cancel new folder"
                  data-testid={`move-folder-dialog-new-cancel-${doc.id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => {
                setCreating(true);
                setSelected(null);
              }}
              className="w-full text-left text-[11px] px-3 py-2 flex items-center gap-2 border border-dashed border-border hover:border-primary hover:text-primary text-text-secondary transition-colors"
              data-testid={`move-folder-dialog-new-${doc.id}`}
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span>Create new folder</span>
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 type-body uppercase tracking-widest"
            onClick={onClose}
            disabled={busy}
            data-testid={`move-folder-dialog-cancel-${doc.id}`}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-8 type-body uppercase tracking-widest"
            disabled={!canSave || busy}
            onClick={handleSave}
            data-testid={`move-folder-dialog-save-${doc.id}`}
          >
            {busy ? 'Moving…' : 'Move'}
          </Button>
        </div>
      </div>
    </div>
  );
};
